import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

const STAGES = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'waiting_on_art', label: 'Waiting on Art' },
  { key: 'waiting_on_blanks', label: 'Waiting on Blanks' },
  { key: 'pre_production', label: 'Pre-Production' },
  { key: 'production', label: 'In Production' },
  { key: 'ready_to_ship', label: 'Ready to Ship' },
  { key: 'completed', label: 'Completed' },
];
const LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

// Local-date YYYY-MM-DD (NOT toISOString, which shifts to UTC and can land
// a job one cell off / off-grid for users behind UTC). Matches the raw date
// string Postgres returns for a DATE column.
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function Production() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && (user.role === 'admin' || user.role === 'manager');
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState('grid'); // 'grid' | 'board'
  const [tab, setTab] = useState('active'); // 'active' | 'archived'
  const [vendorFilter, setVendorFilter] = useState('');
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  async function load() {
    const res = await apiFetch(tab === 'archived' ? '/api/production/archived' : '/api/production');
    if (res.ok) { const d = await res.json(); setJobs(d.jobs); }
  }
  useEffect(() => { load(); }, [tab]);

  const vendors = useMemo(() => {
    const s = new Set(jobs.map((j) => j.vendor_name).filter(Boolean));
    return Array.from(s).sort();
  }, [jobs]);

  const filtered = useMemo(
    () => (vendorFilter ? jobs.filter((j) => j.vendor_name === vendorFilter) : jobs),
    [jobs, vendorFilter]
  );

  async function advance(job, toStatus) {
    const res = await apiFetch(`/api/production/${job.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: toStatus }),
    });
    if (res.ok) load();
  }

  async function setArchived(job, archived, e) {
    if (e) e.stopPropagation();
    const res = await apiFetch(`/api/production/${job.id}/archive`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived }),
    });
    if (res.ok) load();
  }

  // ---- Month grid ----------------------------------------------------------
  function MonthGrid() {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const byDate = {};
    filtered.forEach((j) => {
      if (!j.ship_by_date) return;
      const key = String(j.ship_by_date).slice(0, 10);
      (byDate[key] = byDate[key] || []).push(j);
    });

    const todayKey = ymd(new Date());
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const key = ymd(new Date(year, month, day));
      cells.push({ day, key, jobs: byDate[key] || [] });
    }

    const monthName = first.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    return (
      <div>
        {(() => {
          const undated = filtered.filter((j) => !j.ship_by_date);
          if (!undated.length) return null;
          return (
            <div className="hq-cal-unscheduled">
              <div className="hq-cal-unsched-label">Unscheduled (no ship-by date)</div>
              <div className="hq-cal-unsched-jobs">
                {undated.map((j) => (
                  <div key={j.id} className={`hq-cal-job hq-stage-${j.status}`}
                    onClick={() => navigate(`/production/${j.id}`)} title={`${j.po_number} · set a ship-by date`}>
                    {j.po_number || "—"} · {j.job_title || j.vendor_name || ""}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div className="hq-cal-head">
          <button className="hq-btn" onClick={() => setCursor(new Date(year, month - 1, 1))}>‹</button>
          <h2 style={{ margin: 0 }}>{monthName}</h2>
          <button className="hq-btn" onClick={() => setCursor(new Date(year, month + 1, 1))}>›</button>
        </div>
        <div className="hq-cal-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="hq-cal-dow">{d}</div>
          ))}
          {cells.map((c, idx) => (
            <div key={idx} className={`hq-cal-cell${c ? '' : ' hq-cal-empty'}${c && c.key === todayKey ? ' hq-cal-today' : ''}`}>
              {c && <div className="hq-cal-daynum">{c.day}</div>}
              {c && c.jobs.map((j) => (
                <div key={j.id} className={`hq-cal-job hq-stage-${j.status}`}
                  onClick={() => navigate(`/production/${j.id}`)} title={`${j.po_number} · ${LABEL[j.status]}`}>
                  {j.po_number || '—'} · {j.job_title || j.vendor_name || ''}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- Kanban board --------------------------------------------------------
  function Board() {
    const byStage = {};
    STAGES.forEach((s) => { byStage[s.key] = []; });
    filtered.forEach((j) => { (byStage[j.status] = byStage[j.status] || []).push(j); });
    return (
      <div className="hq-kanban">
        {STAGES.map((s, si) => (
          <div key={s.key} className="hq-kanban-col">
            <div className="hq-kanban-colhead">{s.label} <span>{byStage[s.key].length}</span></div>
            {byStage[s.key].map((j) => (
              <div key={j.id} className={`hq-kanban-card hq-stage-${j.status}`}>
                <div className="hq-kanban-cardtop" onClick={() => navigate(`/production/${j.id}`)}>
                  <strong>{j.po_number || '—'}</strong>
                  <span>{j.job_title || j.vendor_name || ''}</span>
                </div>
                {canManage && si < STAGES.length - 1 && (
                  <button className="hq-kanban-adv" onClick={() => advance(j, STAGES[si + 1].key)}>
                    → {STAGES[si + 1].label}
                  </button>
                )}
              </div>
            ))}
            {byStage[s.key].length === 0 && <div className="hq-kanban-empty">—</div>}
          </div>
        ))}
      </div>
    );
  }

  function ArchivedList() {
    function fmt(s) { if (!s) return '\u2014'; try { return new Date(s).toLocaleDateString(); } catch { return '\u2014'; } }
    if (!filtered.length) return <p className="hq-empty" style={{ padding: 0 }}>No archived jobs.</p>;
    return (
      <table className="hq-table">
        <thead><tr><th>PO</th><th>Title</th><th>Vendor</th><th>Status</th><th>Archived</th><th></th></tr></thead>
        <tbody>
          {filtered.map((j) => (
            <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/production/${j.id}`)}>
              <td>{j.po_number || '\u2014'}</td>
              <td>{j.job_title || '\u2014'}</td>
              <td>{j.vendor_name || '\u2014'}</td>
              <td><span className={`hq-pill hq-stage-${j.status}`}>{LABEL[j.status] || j.status}</span></td>
              <td style={{ color: 'var(--hq-muted)', fontSize: 13 }}>{j.archived_at ? fmt(j.archived_at) : 'auto (14d)'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {canManage && (
                  <button className="hq-link-btn" onClick={(e) => setArchived(j, false, e)}>Unarchive</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Production Calendar</h1>
        <p>Jobs added from purchase orders. Completed jobs auto-archive after 14 days; you can also archive a completed job early.</p>
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className={`hq-btn${view === 'grid' ? '' : ' hq-btn-ghost'}`} onClick={() => setView('grid')}>Calendar</button>
            <button className={`hq-btn${view === 'board' ? '' : ' hq-btn-ghost'}`} onClick={() => setView('board')}>Board</button>
            <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--hq-line)', margin: '0 4px' }} />
            <button className={`hq-btn${tab === 'active' ? '' : ' hq-btn-ghost'}`} onClick={() => setTab('active')}>Active</button>
            <button className={`hq-btn${tab === 'archived' ? '' : ' hq-btn-ghost'}`} onClick={() => setTab('archived')}>Archived</button>
          </div>
          <select className="hq-select" value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
            <option value="">All vendors</option>
            {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={{ padding: 20 }}>
          <div className="hq-legend">
            {STAGES.map((st) => (
              <span key={st.key} className="hq-legend-item">
                <span className={`hq-legend-dot hq-stage-${st.key}`} />
                {st.label}
              </span>
            ))}
          </div>
          {tab === 'archived' ? (
            <ArchivedList />
          ) : jobs.length === 0 ? (
            <p className="hq-empty" style={{ padding: 0 }}>No production jobs yet. Add one from a purchase order.</p>
          ) : (view === 'grid' ? <MonthGrid /> : <Board />)}
        </div>
      </div>
    </div>
  );
}
